import { useSelector } from "react-redux";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import {
  addMedia,
  getElevenlabsVoices,
  getPreferredElevenLabsVoice,
  mediaSelector,
  resetTtsPreview,
  duplicateAvatar,
} from "../state/gallerySlice";
import { useTranslation } from "react-i18next";
import { Dropdown } from "src/common/components/Input";
import { MediaNameInput } from "src/common/components/Input/MediaNameInput";
import { useEffect, useState } from "react";
import { loadingSelector, LoadingState } from "src/common/state/slice/appStateSlice";
import { RootState } from "src/common/state/store";
import { GENERATE_MEDIA_PANEL_COMPONENT } from "./GenerateMediaPanel";
import { useAppDispatch } from "src/common/state/hooks";
import { getCurrentModalStateSlider } from "src/common/state/slice/modal/modalSlice";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import { IMedia } from "src/model/unityObject";
import { trackEvent } from "src/trackEvent";

export const CreateAvatar = ({ closeSliderAnimation }: { closeSliderAnimation(): any }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const mapElevenlabsVoices = () => {
    return elevenLabsVoices.map((voice) => {
      const localizedVoiceGender = t(`general.${voice.gender}`);
      return {
        ...voice,
        value: voice.id,
        optionText: `${voice.name} (${localizedVoiceGender})`,
        gender: voice.gender,
      };
    });
  };

  const openSliderId = useSelector(getCurrentModalStateSlider);
  const elevenLabsVoices = useSelector(getElevenlabsVoices);

  const { loading } = useSelector((state: RootState) => {
    return {
      loading: loadingSelector(state, GENERATE_MEDIA_PANEL_COMPONENT),
    };
  });

  const [mediaNameToEdit, setMediaNameToEdit] = useState<string>("");
  const [tagEditorKey, setTagEditorKey] = useState(0);
  const fetchedVoices = mapElevenlabsVoices();
  const [voiceOptionsFR, setVoiceOptionsFR] = useState(
    fetchedVoices.filter((elt) => elt.language === "fr" || elt.tts_provider === "elevenLabs"),
  );
  const [voiceOptionsEN, setVoiceOptionsEN] = useState(
    fetchedVoices.filter((elt) => elt.language === "en" || elt.tts_provider === "elevenLabs"),
  );
  const [voiceOptionsES, setVoiceOptionsES] = useState(
    fetchedVoices.filter((elt) => elt.language === "es" || elt.tts_provider === "elevenLabs"),
  );
  const defaultTtsVoice = useSelector(getPreferredElevenLabsVoice);
  const medias = useSelector(mediaSelector);

  // Filter avatars from organization 3
  const availableAvatars = medias.filter(
    (m: IMedia) => m.type === "avatar" && m.organization?.id === 3,
  );

  type VoiceSelection = {
    voiceId: string;
    ttsProvider: string;
    name?: string;
    gender?: string;
  };

  const [voiceFr, setVoiceFr] = useState<VoiceSelection>(defaultTtsVoice);
  const [voiceEn, setVoiceEn] = useState<VoiceSelection>(defaultTtsVoice);
  const [voiceEs, setVoiceEs] = useState<VoiceSelection>(defaultTtsVoice);
  const [isSaving, setIsSaving] = useState(false);
  const [nameUnique, setNameUnique] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState<IMedia | null>(null);

  const resetAvatar = () => {
    setMediaNameToEdit("");
    dispatch(resetTtsPreview());
    setTagEditorKey(tagEditorKey + 1);

    setVoiceFr(defaultTtsVoice);
    setVoiceEn(defaultTtsVoice);
    setVoiceEs(defaultTtsVoice);

    setSelectedAvatar(null);

    const fetchedVoices = mapElevenlabsVoices();
    setVoiceOptionsFR(
      fetchedVoices.filter((elt) => elt.language === "fr" || elt.tts_provider === "elevenLabs"),
    );
    setVoiceOptionsEN(
      fetchedVoices.filter((elt) => elt.language === "en" || elt.tts_provider === "elevenLabs"),
    );
    setVoiceOptionsES(
      fetchedVoices.filter((elt) => elt.language === "es" || elt.tts_provider === "elevenLabs"),
    );
  };

  useEffect(() => {
    resetAvatar();
  }, [openSliderId]);

  const saveAvatar = async () => {
    if (!selectedAvatar || isSaving) return;

    const nameNotAvailable = medias.find((m: IMedia) => m.name === mediaNameToEdit);

    if (nameNotAvailable) {
      setNameUnique(false);
      return;
    } else {
      setNameUnique(true);
    }

    trackEvent("avatar-create");
    setIsSaving(true);
    try {
      await dispatch(
        duplicateAvatar({
          avatarId: selectedAvatar.id,
          name: mediaNameToEdit,
          voices: [
            {
              voiceId: voiceFr.voiceId,
              lang: "fr",
              gender: voiceFr.gender || "unknown",
              prettyVoiceName: voiceFr.name || "unknown",
              tts_provider: voiceFr.ttsProvider,
            },
            {
              voiceId: voiceEn.voiceId,
              lang: "en",
              gender: voiceEn.gender || "unknown",
              prettyVoiceName: voiceEn.name || "unknown",
              tts_provider: voiceEn.ttsProvider,
            },
            {
              voiceId: voiceEs.voiceId,
              lang: "es",
              gender: voiceEs.gender || "unknown",
              prettyVoiceName: voiceEs.name || "unknown",
              tts_provider: voiceEs.ttsProvider,
            },
          ],
          componentId: GENERATE_MEDIA_PANEL_COMPONENT,
        }),
      ).then((res) => {
        if (res?.payload) {
          dispatch(addMedia(res.payload));
        }
        closeSliderAnimation();
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-1 h-full min-h-0 w-full overflow-x-hidden flex flex-col">
      <IsLoading
        componentId={GENERATE_MEDIA_PANEL_COMPONENT}
        spinnerPlaceholder
        showSuccess={false}
        spinnerStyle={{ marginTop: "4vh" }}
      >
        <div className="flex-1 min-h-0 w-full overflow-y-scroll overflow-x-hidden rounded-lg bg-gray-100">
          {availableAvatars.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              {t("pages.gallery.noAvatarsAvailable") || "No avatars available"}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 p-4">
              {availableAvatars.map((avatar: IMedia) => (
                <div
                  key={avatar.id}
                  className={`cursor-pointer rounded-xl p-2 transition-all bg-white shadow-md hover:scale-105 hover:shadow-2xl hover:ring-green-500 hover:ring-4 ${
                    selectedAvatar?.id === avatar.id ? "ring-purple-500 ring-4 scale-105" : ""
                  }`}
                  onClick={() => setSelectedAvatar(avatar)}
                >
                  <img
                    src={avatar.thumbnail_s3_url || avatar.s3_url}
                    alt={avatar.name}
                    className="w-full h-32 object-cover rounded"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 mt-4">
          <div>
            <div>
              <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>
              <MediaNameInput
                value={mediaNameToEdit}
                onChange={setMediaNameToEdit}
                maxLength={55}
                disabled={loading === LoadingState.Loading}
              />
              <div className={`text-red-600 ${nameUnique ? "invisible" : ""}`}>
                {t("general.mediaNameAlreadyUsed")}
              </div>
            </div>

            <div className="flex w-full justify-between">
              <Dropdown
                options={voiceOptionsFR}
                label={t("pages.gallery.generateVoicePanel.voice") + " FR"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(_value: string, index: number) => {
                  setVoiceFr({
                    voiceId: voiceOptionsFR[index].id,
                    ttsProvider: voiceOptionsFR[index].tts_provider,
                    name: voiceOptionsFR[index].name,
                    gender: voiceOptionsFR[index].gender,
                  });
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptionsFR.findIndex((option) => option.value === voiceFr.voiceId) || 0
                }
              />

              <Dropdown
                options={voiceOptionsEN}
                label={t("pages.gallery.generateVoicePanel.voice") + " EN"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(_value: string, index: number) => {
                  setVoiceEn({
                    voiceId: voiceOptionsEN[index].id,
                    ttsProvider: voiceOptionsEN[index].tts_provider,
                    name: voiceOptionsEN[index].name,
                    gender: voiceOptionsEN[index].gender,
                  });
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptionsEN.findIndex((option) => option.value === voiceEn.voiceId) || 0
                }
              />

              <Dropdown
                options={voiceOptionsES}
                label={t("pages.gallery.generateVoicePanel.voice") + " ES"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(_value: string, index: number) => {
                  setVoiceEs({
                    voiceId: voiceOptionsES[index].id,
                    ttsProvider: voiceOptionsES[index].tts_provider,
                    name: voiceOptionsES[index].name,
                    gender: voiceOptionsES[index].gender,
                  });
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptionsES.findIndex((option) => option.value === voiceEs.voiceId) || 0
                }
              />
            </div>
            <div className="mt-2">
              <button
                disabled={
                  !selectedAvatar ||
                  isStringNullOrEmpty(mediaNameToEdit) ||
                  mediaNameToEdit.length > 55 ||
                  mediaNameToEdit.trim() === "" ||
                  isSaving
                }
                className={"btn-primary-fill mr-3 mt-2 disabled:cursor-not-allowed"}
                onClick={saveAvatar}
              >
                {t("general.save")}
              </button>
            </div>
          </div>
        </div>
      </IsLoading>
    </div>
  );
};
